import Dropdown from "@/lib/Dropdown";
import { File } from "@/lib/queries/types";
import { truncateTextMiddle } from "@/lib/utils";
import { Tooltip } from "react-tooltip";
import { Dispatch, SetStateAction } from "react";
import { components } from "@/lib/ApiSchema";
import { PendingSavePayload } from "../partTree/PartTreeItem";
import CaretDownIcon from "@/lib/CaretDownIcon";
import classnames from "classnames";
import AlertIcon from "~icons/local/alert";

export default function SelectTechnicalDrawing({
  technicalDrawing,
  technicalDrawings,
  pendingSave,
  setPendingSave,
  disabled = false,
  truncateThreshold,
}: {
  technicalDrawing: components["schemas"]["PartTechnicalDrawingSchema"] | null;
  technicalDrawings: File[];
  pendingSave: PendingSavePayload | undefined;
  setPendingSave: Dispatch<SetStateAction<PendingSavePayload | undefined>>;
  disabled?: boolean;
  truncateThreshold: number;
}) {
  const selectedFileId: string | null =
    pendingSave?.technical_drawing_id ?? technicalDrawing?.id ?? null;

  const tooltipId = Math.random().toString();
  const selectedFileName = technicalDrawings.find(
    ({ id }) => id === selectedFileId,
  )?.file_name;
  const fullSelectedLabel =
    selectedFileName ??
    (disabled ? "No file selected" : "Choose a drawing file");
  const truncatedSelectedLabel = truncateTextMiddle(
    fullSelectedLabel,
    truncateThreshold,
  );

  return (
    <Dropdown
      disabled={disabled}
      trigger={({ expanded }: { expanded: boolean }) => (
        <div
          data-tooltip-id={tooltipId}
          className={classnames(
            "cursor-pointer rounded-lg flex gap-2 px-2 py-1 justify-between items-center",
            {
              "text-gray-500 bg-red-100": !selectedFileId,
            },
          )}
        >
          {selectedFileId === null && (
            <AlertIcon className="text-red-500 size-4" />
          )}
          <div>{truncatedSelectedLabel}</div>
          {!disabled && <CaretDownIcon expanded={expanded} />}
          {fullSelectedLabel !== truncatedSelectedLabel && (
            <Tooltip id={tooltipId}>
              <span className="text-xs">{fullSelectedLabel}</span>
            </Tooltip>
          )}
        </div>
      )}
    >
      <div className="flex flex-col text-xs bg-white p-2 my-2 overflow-y-auto max-h-80 max-w-80">
        {technicalDrawings.map(({ id, file_name }, i) => (
          <div
            key={i}
            className={`rounded-sm truncate shrink-0 m-1 px-4 py-2 whitespace-nowrap hover:bg-slate-100 cursor-pointer select-none ${id === selectedFileId ? "bg-slate-100" : ""}`}
            onClick={() => {
              if (id !== selectedFileId) {
                setPendingSave((pre) => {
                  return {
                    ...pre,
                    technical_drawing_id: id,
                  };
                });
              }
            }}
            title={file_name}
          >
            {file_name}
          </div>
        ))}
        {technicalDrawings.length === 0 && <div>No File</div>}
      </div>
    </Dropdown>
  );
}
